import React, { useCallback, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import { CsvDelimiter } from '../../utils/csvDelimiter';
import { parseSpreadsheetFile } from '../../utils/spreadsheetParser';

interface ExcelData {
  headers: string[];
  rows: string[][];
  totalRows: number;
  delimiter?: CsvDelimiter;
}

interface ExcelFileUploadProps {
  onFileUpload: (file: File, data: ExcelData) => void;
  maxFileSize: number; // in MB
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const ACCEPTED_EXTENSIONS = ['.xlsx', '.xls', '.csv'];

const ExcelFileUpload: React.FC<ExcelFileUploadProps> = ({
  onFileUpload,
  maxFileSize,
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0) return;

      const file = acceptedFiles[0];
      setError(null);
      setIsProcessing(true);
      setPendingFile(file);

      try {
        // Yield to the event loop so the UI renders the parsing state before blocking
        await new Promise(resolve => setTimeout(resolve, 50));

        const excelData = await parseSpreadsheetFile(file);

        onFileUpload(file, excelData);
        setIsProcessing(false);
        setPendingFile(null);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to process file'
        );
        setIsProcessing(false);
        setPendingFile(null);
      }
    },
    [onFileUpload]
  );

  const { getRootProps, getInputProps, isDragActive, fileRejections } =
    useDropzone({
      onDrop,
      accept: {
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
        'application/vnd.ms-excel': ['.xls'],
        'text/csv': ['.csv'],
      },
      maxSize: maxFileSize * 1024 * 1024,
      multiple: false,
      disabled: isProcessing,
    });


  return (
    <Box>
      {/* File rejection errors — shown above the dropzone */}
      {fileRejections.length > 0 && (
        <Alert severity="warning" sx={{ mb: 1.5, py: 0, '& .MuiAlert-message': { py: 0.75 } }}>
          {fileRejections.map(({ file, errors }) => (
            <Typography key={file.name} variant="body2">
              <strong>{file.name}</strong> — {errors.map(e =>
                e.code === 'file-too-large'
                  ? `File is larger than ${maxFileSize} MB`
                  : e.message
              ).join(', ')}
            </Typography>
          ))}
        </Alert>
      )}

      {/* Drop zone */}
      <Box
        {...getRootProps()}
        sx={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 220,
          p: 4,
          borderRadius: 3,
          border: '2px dashed',
          borderColor: (() => {
            if (isDragActive) return 'primary.main';
            if (isProcessing) return 'primary.light';
            return 'grey.300';
          })(),
          bgcolor: (() => {
            if (isDragActive) return 'primary.50';
            if (isProcessing) return 'grey.50';
            return 'background.default';
          })(),
          cursor: isProcessing ? 'default' : 'pointer',
          transition: 'all 0.2s ease-in-out',
          outline: 'none',
          '&:hover': {
            borderColor: isProcessing ? 'primary.light' : 'primary.main',
            bgcolor: isProcessing ? 'grey.50' : 'primary.50',
          },
        }}
      >
        <input {...getInputProps()} />

        {(() => {
          if (isProcessing) return (
          /* Parsing state */
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress size={48} sx={{ mb: 2 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
              {pendingFile?.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {pendingFile ? formatBytes(pendingFile.size) : ''} · Parsing file…
            </Typography>
          </Box>
          );
          if (isDragActive) return (
          /* Drag-active state */
          <Box sx={{ textAlign: 'center' }}>
            <CloudUploadIcon
              sx={{ fontSize: 56, color: 'primary.main', mb: 1.5 }}
            />
            <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
              Drop to upload
            </Typography>
            <Typography variant="body2" color="primary.main">
              Release your file here
            </Typography>
          </Box>
          );
          return (
          /* Idle state */
          <Box sx={{ textAlign: 'center' }}>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                bgcolor: 'grey.100',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2,
                transition: 'all 0.2s',
              }}
            >
              <CloudUploadIcon sx={{ fontSize: 32, color: 'grey.500' }} />
            </Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
              Drag & drop your file here
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
              or click anywhere to browse
            </Typography>

            <Box sx={{ display: 'flex', gap: 0.75, justifyContent: 'center', mb: 2.5 }}>
              {ACCEPTED_EXTENSIONS.map(ext => (
                <Chip
                  key={ext}
                  label={ext}
                  size="small"
                  variant="outlined"
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: '0.7rem',
                    borderColor: 'grey.300',
                    color: 'text.secondary',
                  }}
                />
              ))}
            </Box>

            <Button
              variant="outlined"
              size="small"
              startIcon={<CloudUploadIcon />}
              sx={{ pointerEvents: 'none' }}
            >
              Choose File
            </Button>

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 2 }}
            >
              Max file size: {maxFileSize} MB
            </Typography>
          </Box>
          );
        })()}
      </Box>

      {/* Errors */}
      {error && (
        <Alert severity="error" sx={{ mt: 2, py: 0.5 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}


    </Box>
  );
};

export default ExcelFileUpload;
